// Sample payloads for the gated demo view.
//
// Every object here matches the exact client-safe response shape produced by the
// authenticated BFF adapters (`server/adapters/operating.ts` and
// `server/connectionsService.ts`) so the UI is exercised the same way it is in a
// live workspace. These values are illustrative only and are never used when a
// real session is present.

export const DEMO_WORKSPACE_NAME = "Aurora Home Goods";
export const DEMO_CLIENT_SLUG = "demo-workspace";

export const DEMO_MEMBER = Object.freeze({
  id: "demo-member",
  email: "demo@growthos.example",
});

export const DEMO_CLIENT = Object.freeze({
  clientId: "demo-client",
  clientSlug: DEMO_CLIENT_SLUG,
  clientName: DEMO_WORKSPACE_NAME,
  role: "owner",
});

export const DEMO_MEMBERSHIPS = Object.freeze([DEMO_CLIENT]);

function hoursAgo(hours) {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

const BULK_BACKFILL_AT = hoursAgo(30);

export function buildDemoAgentProfile() {
  return {
    schemaVersion: 1,
    clientSlug: DEMO_CLIENT_SLUG,
    agent: {
      name: "Piper",
      runtimeStatus: "unknown",
      runtimeStatusReason: "A client-safe runtime heartbeat is not connected yet.",
    },
    onboarding: {
      sourceStatus: "available",
      status: "active",
      currentPhase: "Operating",
      routeActive: true,
      blocker: null,
      updatedAt: hoursAgo(52),
      operatingBriefSourceStatus: "available",
      operatingBriefApproved: true,
      operatingBriefVersion: 4,
    },
    editableSettings: false,
    settingsNote:
      "Name and image editing will appear here once durable profile storage is connected. User permissions live in Team & Access.",
    generatedAt: new Date().toISOString(),
  };
}

const DEMO_SKILL_ROWS = [
  {
    id: "skill-brief-writer",
    name: "Brand Brief Writer",
    description: "Turns a rough ask into a one-page brief: audience, goal, offer, proof, and the approval step. Use it before any campaign, page, or flow so the request is clear.",
    scope: "stock_hermes",
    status: "active",
    version: 6,
    lastAction: "updated",
    whatChanged: "Tightened the brief template so every request states the audience and the approval step.",
    whyChanged: "Briefs were being started without a named audience.",
    promotionStatus: null,
    driftStatus: null,
    changedAt: hoursAgo(120),
  },
  {
    id: "skill-research",
    name: "Market Research Assistant",
    description: "Gathers what is being said about a category, competitor, or product and returns a short, sourced summary. Use it when you need the lay of the land before deciding.",
    scope: "stock_hermes",
    status: "active",
    version: 3,
    lastAction: "installed",
    whatChanged: null,
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: hoursAgo(300),
  },
  {
    id: "skill-summarizer",
    name: "Thread Summarizer",
    description: "Reads a long Slack thread or document and returns the decisions, open questions, and owners. Use it to catch up or to close a discussion cleanly.",
    scope: "stock",
    status: "active",
    version: 2,
    lastAction: "updated",
    whatChanged: "Summaries now separate decisions from open questions.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: hoursAgo(210),
  },
  {
    id: "skill-email-core",
    name: "GrowthOS Email Skill",
    description: "Drafts email campaigns in your brand's voice with subject-line options and a send checklist. Use it for promotions, launches, and announcements; nothing sends without your approval.",
    scope: "growthos",
    status: "active",
    version: 11,
    lastAction: "updated",
    whatChanged: "Added a subject-line variant step and a required approval checkpoint before scheduling.",
    whyChanged: "Campaign drafts needed a clearer review boundary before send.",
    promotionStatus: "promoted",
    driftStatus: null,
    changedAt: hoursAgo(26),
  },
  {
    id: "skill-lifecycle",
    name: "GrowthOS Lifecycle Flow Skill",
    description: "Plans trigger-based journeys (welcome, abandoned cart, post-purchase, win-back) with timing, branches, and draft messages. Use it when you want a flow designed before anyone builds it.",
    scope: "growthos",
    status: "active",
    version: 8,
    lastAction: "updated",
    whatChanged: "Journey maps now include exit criteria for every branch.",
    whyChanged: "Flows were being approved without an exit path.",
    promotionStatus: null,
    driftStatus: null,
    changedAt: BULK_BACKFILL_AT,
  },
  {
    id: "skill-landing",
    name: "GrowthOS Landing Page Skill",
    description: "Reviews or drafts a landing page around one offer: headline, proof, structure, and the conversion path. Use it for new pages and for pages that are not converting.",
    scope: "shared",
    status: "active",
    version: 5,
    lastAction: "updated",
    whatChanged: "Conversion reviews are ranked by expected impact and effort.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: BULK_BACKFILL_AT,
  },
  {
    id: "skill-paid-media",
    name: "GrowthOS Paid Media Skill",
    description: "Audits ad accounts and proposes campaigns, audiences, and creative angles with a budget rationale. Use it for Meta and Google Ads planning; changes stay proposals until you approve.",
    scope: "growthos",
    status: "active",
    version: 4,
    lastAction: "updated",
    whatChanged: "Budget-change proposals are always returned for approval instead of applied.",
    whyChanged: "Paid changes must stay approval gated.",
    promotionStatus: null,
    driftStatus: null,
    changedAt: BULK_BACKFILL_AT,
  },
  {
    id: "skill-reporting",
    name: "GrowthOS Reporting Skill",
    description: "Pulls the numbers that matter from connected tools and explains what moved and why. Use it for weekly reports, quick checks, and board-ready summaries.",
    scope: "growthos",
    status: "active",
    version: 7,
    lastAction: "updated",
    whatChanged: "Reports now name any stale or missing source instead of filling the gap.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: BULK_BACKFILL_AT,
  },
  {
    id: "skill-social",
    name: "GrowthOS Social Content Skill",
    description: "Plans and drafts social posts in batches with a calendar, hooks, and captions in your voice. Use it to fill a month of content or react to a moment.",
    scope: "growthos",
    status: "active",
    version: 3,
    lastAction: "updated",
    whatChanged: "Batches are returned as a reviewable calendar.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: BULK_BACKFILL_AT,
  },
  {
    id: "skill-merch",
    name: "GrowthOS Merchandising Skill",
    description: "Reviews collections, product order, bundles, and pricing against your sales data. Use it before a season change or when a category is underperforming.",
    scope: "growthos",
    status: "active",
    version: 2,
    lastAction: "installed",
    whatChanged: null,
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: BULK_BACKFILL_AT,
  },
  {
    id: "skill-aurora-email",
    name: "Aurora Email Voice",
    description: "Writes Aurora Home Goods emails in the warm, plain-spoken style your team approved, with the subject-line patterns that have performed best. Use it for every customer email.",
    scope: "brand_local",
    status: "active",
    version: 9,
    lastAction: "updated",
    whatChanged: "Learned the approved warm, plain-spoken subject-line style from the last six approved sends.",
    whyChanged: "Approved campaigns consistently used shorter, benefit-led subject lines.",
    promotionStatus: "candidate",
    driftStatus: null,
    changedAt: hoursAgo(5),
  },
  {
    id: "skill-aurora-bundles",
    name: "Aurora Bundle Merchandiser",
    description: "Suggests room-based bundles from your catalog and recent orders, with pricing that protects margin. Use it when planning promotions or refreshing the bundles page.",
    scope: "workspace_customized",
    status: "active",
    version: 4,
    lastAction: "updated",
    whatChanged: "Bundle suggestions now prefer the room-based sets your team approved in spring.",
    whyChanged: "Room-based bundles outperformed single-product upsells.",
    promotionStatus: null,
    driftStatus: "drifted",
    changedAt: hoursAgo(9),
  },
  {
    id: "skill-aurora-launch",
    name: "Aurora Launch Checklist",
    description: "Runs a product launch through your own checklist, including the retail partner lead time and the approvals a launch needs. Use it from the first launch idea to launch day.",
    scope: "brand_specific",
    status: "active",
    version: 2,
    lastAction: "created",
    whatChanged: "Created from your last two launches, including the retail partner lead time.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: hoursAgo(72),
  },
  {
    id: "skill-retired-sms",
    name: "Legacy SMS Blaster",
    description: "Sent one-off SMS campaigns without segmentation. Retired in favour of the lifecycle flow skill.",
    scope: "brand_local",
    status: "deleted",
    version: 1,
    lastAction: "deleted",
    whatChanged: "Removed in favour of the GrowthOS Lifecycle Flow Skill.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    changedAt: hoursAgo(400),
  },
];

const DEMO_SKILL_CHANGES = [
  {
    id: "change-1",
    skillName: "Aurora Email Voice",
    scope: "brand_local",
    action: "updated",
    occurredAt: hoursAgo(5),
    whatChanged: "Learned the approved warm, plain-spoken subject-line style from the last six approved sends.",
    whyChanged: "Approved campaigns consistently used shorter, benefit-led subject lines.",
    promotionStatus: "candidate",
    driftStatus: null,
    rollbackAvailable: true,
  },
  {
    id: "change-2",
    skillName: "Aurora Bundle Merchandiser",
    scope: "workspace_customized",
    action: "updated",
    occurredAt: hoursAgo(9),
    whatChanged: "Bundle suggestions now prefer the room-based sets your team approved in spring.",
    whyChanged: "Room-based bundles outperformed single-product upsells.",
    promotionStatus: null,
    driftStatus: "drifted",
    rollbackAvailable: true,
  },
  {
    id: "change-3",
    skillName: "GrowthOS Email Skill",
    scope: "growthos",
    action: "updated",
    occurredAt: hoursAgo(26),
    whatChanged: "Added a subject-line variant step and a required approval checkpoint before scheduling.",
    whyChanged: "Campaign drafts needed a clearer review boundary before send.",
    promotionStatus: "promoted",
    driftStatus: null,
    rollbackAvailable: false,
  },
  ...[
    "GrowthOS Lifecycle Flow Skill",
    "GrowthOS Landing Page Skill",
    "GrowthOS Paid Media Skill",
    "GrowthOS Reporting Skill",
    "GrowthOS Social Content Skill",
    "GrowthOS Merchandising Skill",
  ].map((skillName, index) => ({
    id: `change-bulk-${index}`,
    skillName,
    scope: "growthos",
    action: "updated",
    occurredAt: BULK_BACKFILL_AT,
    whatChanged: "Shared GrowthOS package refresh applied to every eligible workspace.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    rollbackAvailable: false,
  })),
  {
    id: "change-10",
    skillName: "Aurora Launch Checklist",
    scope: "brand_specific",
    action: "created",
    occurredAt: hoursAgo(72),
    whatChanged: "Created from your last two launches, including the retail partner lead time.",
    whyChanged: null,
    promotionStatus: null,
    driftStatus: null,
    rollbackAvailable: false,
  },
  {
    id: "change-11",
    skillName: "Brand Brief Writer",
    scope: "stock_hermes",
    action: "updated",
    occurredAt: hoursAgo(120),
    whatChanged: "Tightened the brief template so every request states the audience and the approval step.",
    whyChanged: "Briefs were being started without a named audience.",
    promotionStatus: null,
    driftStatus: null,
    rollbackAvailable: false,
  },
  // Older history for one brand skill, so a skill's changes over time can be reviewed.
  {
    id: "change-12",
    skillName: "Aurora Bundle Merchandiser",
    scope: "brand_specific",
    action: "updated",
    occurredAt: hoursAgo(24 * 12),
    whatChanged: "Bundle pricing now keeps a minimum margin per bundle instead of per item.",
    whyChanged: "Two spring bundles had been priced below the margin floor.",
    promotionStatus: null,
    driftStatus: null,
    rollbackAvailable: false,
  },
  {
    id: "change-13",
    skillName: "Aurora Bundle Merchandiser",
    scope: "brand_specific",
    action: "created",
    occurredAt: hoursAgo(24 * 31),
    whatChanged: "Created from the shared merchandising skill with Aurora's catalog and room categories.",
    whyChanged: "Aurora sells by room, and the shared skill grouped by product type.",
    promotionStatus: null,
    driftStatus: null,
    rollbackAvailable: false,
  },
];

export function buildDemoSkills() {
  return {
    schemaVersion: 1,
    skills: DEMO_SKILL_ROWS.map((skill) => ({ ...skill })),
    changes: DEMO_SKILL_CHANGES.map((change) => ({ ...change })),
    updateAvailability: {
      status: "unavailable",
      reason: "No approved release catalog is connected yet.",
    },
    generatedAt: new Date().toISOString(),
  };
}

export function buildDemoConnections() {
  const groups = {
    commerce: { key: "commerce", label: "Commerce", description: "Storefront, marketplace, and seller systems", tone: "commerce" },
    marketing: { key: "marketing", label: "Marketing", description: "Campaign, lifecycle, and paid media systems", tone: "marketing" },
    creative: { key: "creative", label: "Creative", description: "Approved creative production systems", tone: "creative" },
  };
  const baseLifecycle = {
    connectionGeneration: 0,
    cleanupGeneration: 0,
    connected: false,
    identityVerified: false,
    businessReadVerified: false,
    persistenceVerified: false,
    ready: false,
  };
  const connection = ({
    key,
    label,
    group,
    description,
    phase = "authorization_ready",
    generation = 0,
    account = null,
    action = "connect",
    blocker = null,
    proofs = {},
    disconnect = false,
  }) => ({
    key,
    label,
    description,
    group: groups[group],
    connectionType: "guided",
    status: phase,
    readOnlyFirst: true,
    account,
    lifecycle: {
      ...baseLifecycle,
      phase,
      connectionGeneration: generation,
      connected: Boolean(account),
      ...proofs,
    },
    capabilities: {
      status: true,
      connect: action === "connect",
      reconnect: action === "reconnect",
      test: action === "test",
      disconnect,
    },
    action: action
      ? { action: action === "test" ? "test" : "authorize", operation: action, canStart: action !== "test", blocker }
      : { action: phase === "setup_required" ? "setup_required" : "view_only", operation: null, canStart: false, blocker },
  });

  return {
    schemaVersion: 2,
    generatedAt: new Date().toISOString(),
    client: { clientId: DEMO_CLIENT.clientId, clientSlug: DEMO_CLIENT_SLUG, clientName: DEMO_WORKSPACE_NAME },
    canManage: true,
    providers: [
      connection({
        key: "shopify",
        label: "Shopify",
        group: "commerce",
        description: "Store and customer context for approved commerce actions.",
        phase: "ready",
        generation: 7,
        account: { label: "Demo Store" },
        action: "test",
        disconnect: true,
        proofs: { identityVerified: true, businessReadVerified: true, persistenceVerified: true, ready: true },
      }),
      connection({
        key: "amazon_seller",
        label: "Amazon Seller Central",
        group: "commerce",
        description: "Seller identity and marketplace context for approved business reads.",
      }),
      connection({
        key: "meta_ads",
        label: "Meta Ads",
        group: "marketing",
        description: "Sign in with Facebook to connect your ad accounts. GrowthOS reads campaigns, ad sets, ads and insights; spend and edits stay approval-gated.",
        phase: "ready",
        generation: 4,
        account: { label: "Demo Ad Account" },
        action: "test",
        disconnect: true,
        proofs: { identityVerified: true, businessReadVerified: true, persistenceVerified: true, ready: true },
      }),
      connection({
        key: "klaviyo",
        label: "Klaviyo",
        group: "marketing",
        description: "Audience, list, campaign, and performance context for approved marketing actions.",
        phase: "identity_verified",
        generation: 3,
        account: { label: "Demo Klaviyo" },
        action: "test",
        disconnect: true,
        proofs: { identityVerified: true },
      }),
      connection({
        key: "google_ads",
        label: "Google Ads",
        group: "marketing",
        description: "Advertising account context through a guided connection.",
        phase: "reauthorization_required",
        generation: 2,
        account: { label: "Demo Google Ads" },
        action: "reconnect",
        disconnect: true,
      }),
      connection({
        key: "higgsfield",
        label: "Higgsfield",
        group: "creative",
        description: "Creative generation access for approved workspace actions.",
        phase: "setup_required",
        action: null,
        blocker: "Creative access is still being prepared for this workspace.",
      }),
    ],
  };
}

// The contract fixture (connections-ui-20261007/CONTRACT.md, section 8): every card kind, every status, every
// add flow. Times are relative so the "checked" lines stay honest.
export function buildDemoAgentConnections() {
  const minutesAgo = (minutes) => new Date(Date.now() - minutes * 60 * 1000).toISOString();
  const card = (app, label, kind, status, extra = {}) => ({ app, label, kind, status, reason: null, lastCheckedAt: minutesAgo(12), envKeys: [], accountLabel: null, toolCount: null, canTest: true, canRemove: true, canReconnect: false, job: null, ...extra });
  return {
    schemaVersion: 1,
    clientSlug: DEMO_CLIENT_SLUG,
    client: { clientId: DEMO_CLIENT.clientId, clientSlug: DEMO_CLIENT_SLUG, clientName: DEMO_WORKSPACE_NAME },
    canManage: true,
    checksAvailable: true,
    lastSweepAt: minutesAgo(5),
    connections: [
      card("meta_ads", "Meta Ads", "api_key", "not_working", { reason: "Meta rejected the token (code 190). Reconnect to issue a new one.", lastCheckedAt: minutesAgo(27), envKeys: ["META_ACCESS_TOKEN", "META_AD_ACCOUNT_IDS"], canReconnect: true }),
      card("shopify", "Shopify", "api_key", "connected", { envKeys: ["SHOPIFY_SHOP", "SHOPIFY_ACCESS_TOKEN"], accountLabel: "aurora-home-goods.myshopify.com" }),
      card("klaviyo", "Klaviyo", "mcp", "connected", { accountLabel: DEMO_WORKSPACE_NAME, toolCount: 41 }),
      card("notion", "Notion", "mcp", "not_working", { reason: "Sign-in expired. Sign in again.", lastCheckedAt: hoursAgo(6), accountLabel: "Aurora HQ", canReconnect: true }),
      card("google_ads", "Google Ads", "pipedream", "connected", { lastCheckedAt: minutesAgo(17), accountLabel: "Aurora Home Goods (123-456-7890)" }),
      card("amazon_seller", "Amazon Seller", "pipedream", "connected", { lastCheckedAt: minutesAgo(17), accountLabel: "Aurora Seller Central" }),
      card("higgsfield", "Ad creative", "higgsfield", "connected", { lastCheckedAt: minutesAgo(9), toolCount: 98 }),
      card("pinterest", "Pinterest", "pipedream", "connected", { lastCheckedAt: hoursAgo(33), accountLabel: "@aurorahome" }),
      card("figma", "Figma", "api_key", "not_verified", { lastCheckedAt: null, envKeys: ["FIGMA_TOKEN"], canTest: false }),
      card("github", "GitHub", "api_key", "connected", { envKeys: ["GH_TOKEN"], accountLabel: "aurorahome", job: { id: "job-1", action: "test", status: "applying", error: null } }),
    ],
    pending: [
      { app: "asana", label: "Asana", job: { id: "demo-job", action: "install", status: "queued", error: null } },
    ],
    addable: {
      apps: [
        { app: "canva", label: "Canva", group: "Creative", flow: "pipedream", fields: [], guide: null },
        { app: "monday", label: "monday.com", group: "Operations", flow: "key", fields: [{ envKey: "MONDAY_API_TOKEN", label: "API token", placeholder: "", secret: true, required: true }], guide: null },
        { app: "hubspot", label: "HubSpot", group: "Marketing", flow: "mcp", fields: [], guide: null },
        { app: "google_analytics", label: "Google Analytics", group: "Analytics", flow: "pipedream", fields: [], guide: null },
        { app: "google_sheets", label: "Google Sheets", group: "Google", flow: "pipedream", fields: [], guide: null },
        { app: "gmail", label: "Gmail", group: "Google", flow: "pipedream", fields: [], guide: null },
        { app: "trustpilot", label: "Trustpilot", group: "Reviews", flow: "chat", fields: [{ name: "TRUSTPILOT_API_KEY", label: "API key", secret: true }], guide: { title: "Where to find it", steps: ["Open Trustpilot Business", "Integrations → API", "Create a key"], url: "https://businessapp.b2b.trustpilot.com" } },
        { app: "vercel", label: "Vercel", group: "Engineering", flow: "key", fields: [{ name: "VERCEL_TOKEN", label: "Token", secret: true }], guide: null },
        { app: "loop", label: "Loop Subscriptions", group: "Commerce", flow: "key", fields: [{ envKey: "LOOP_SUBSCRIPTIONS_API_TOKEN", label: "API token", placeholder: "", secret: true, required: true }], guide: null },
      ],
      customKeyPattern: "^[A-Z][A-Z0-9_]{2,63}$",
    },
  };
}

